import React, { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Loader2, CreditCard, Calendar, User, FileText, Hash } from 'lucide-react'
import { SimpleAccountSelector } from '@/app/components/common/SimpleAccountSelector'
import { VoucherResponse } from '@/app/types/voucher'
import { format, parseISO } from 'date-fns'
import { toast } from 'sonner'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { recordPaymentIn } from '@/app/services/paymentService'
import { PaymentReceivedMultiInvoiceCreate } from '@/app/types/payment'
import { getSimplifiedSalesInvoiceStatus } from '@/app/lib/statusUtils'
import { useAccountFilters } from '@/app/hooks/useAccountFilters'

interface QuickPaymentModalProps {
  isOpen: boolean
  onClose: () => void
  invoice: VoucherResponse
}

// Helper function to format currency
const formatCurrency = (amount: number | undefined | null): string => {
  if (amount === undefined || amount === null) return 'N/A'
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR'
  }).format(Number(amount))
}

// Helper function to format date
const formatDate = (dateString: string | Date | undefined | null): string => {
  if (!dateString) return 'N/A'
  try {
    const date = typeof dateString === 'string' ? parseISO(dateString) : dateString
    return format(date, 'dd MMM yyyy')
  } catch (error) {
    return String(dateString)
  }
}

// Detail item component
interface DetailItemProps {
  icon: React.ElementType
  label: string
  value: React.ReactNode
  className?: string
}

const DetailItem: React.FC<DetailItemProps> = ({ icon: Icon, label, value, className }) => (
  <div className={`flex items-start space-x-2 ${className}`}>
    <Icon className="h-4 w-4 mt-0.5 text-muted-foreground flex-shrink-0" aria-hidden="true" />
    <div className="min-w-0 flex-1">
      <p className="text-sm text-muted-foreground">{label}</p>
      <p className="text-sm font-medium break-words">{value}</p>
    </div>
  </div>
)

export const QuickPaymentModal: React.FC<QuickPaymentModalProps> = ({
  isOpen,
  onClose,
  invoice
}) => {
  const queryClient = useQueryClient()
  const [receivedInAccountId, setReceivedInAccountId] = useState<number | null>(null)
  
  // Get account filters based on user's default site
  const { paymentAccountIds } = useAccountFilters()

  // Calculate amount due - use the invoice's outstanding amount or total amount
  const amountDue = invoice.amount_due ?? invoice.total_amount ?? 0
  const currentStatus = getSimplifiedSalesInvoiceStatus(invoice)

  // Create payment mutation
  const createPaymentMutation = useMutation({
    mutationFn: (data: PaymentReceivedMultiInvoiceCreate) => recordPaymentIn(data),
    onSuccess: () => {
      // Invalidate queries to refresh the UI
      queryClient.invalidateQueries({ queryKey: ['sales-invoices'] })
      queryClient.invalidateQueries({ queryKey: ['sales-invoice', invoice.id] })
      queryClient.invalidateQueries({ queryKey: ['payments'] })

      toast.success(`Payment recorded successfully for Invoice #${invoice.voucher_number}`)
      onClose()
    },
    onError: (error: Error) => {
      toast.error(`Failed to record payment: ${error.message}`)
    }
  })

  const handleConfirmPayment = () => {
    if (!receivedInAccountId) {
      toast.error('Please select a receiving account')
      return
    }

    if (amountDue <= 0) {
      toast.error('Invoice has no outstanding amount')
      return
    }

    // Prepare payment data
    const paymentData: PaymentReceivedMultiInvoiceCreate = {
      party_id: invoice.party_id,
      received_in_account_id: receivedInAccountId,
      total_payment_amount: amountDue,
      transaction_date: format(new Date(), 'yyyy-MM-dd'),
      allocations: [
        {
          invoice_voucher_id: invoice.id,
          amount_to_allocate: amountDue
        }
      ],
      reference_number: `Quick Payment for ${invoice.voucher_number}`,
      narration: `Full payment received for Invoice #${invoice.voucher_number}`
    }

    createPaymentMutation.mutate(paymentData)
  }

  const isSubmitting = createPaymentMutation.isPending

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Mark Invoice as Paid</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4 overflow-y-auto flex-1">
          {/* Invoice Details */}
          <div className="bg-muted/50 p-4 rounded-lg space-y-3">
            <h4 className="font-medium text-sm">Invoice Details</h4>
            <div className="grid grid-cols-2 gap-4">
              <DetailItem icon={Hash} label="Invoice #" value={invoice.voucher_number} />
              <DetailItem
                icon={Calendar}
                label="Invoice Date"
                value={formatDate(invoice.transaction_date)}
              />
              <DetailItem icon={User} label="Customer" value={invoice.party?.name || 'N/A'} />
              <DetailItem icon={FileText} label="Current Status" value={currentStatus} />
            </div>
            <div className="pt-2 border-t">
              <DetailItem
                icon={CreditCard}
                label="Amount Due"
                value={formatCurrency(amountDue)}
                className="text-lg"
              />
            </div>
          </div>

          {/* Receiving Account Selection */}
          <div className="space-y-2">
            <Label htmlFor="receiving-account">
              Receiving Account <span className="text-destructive">*</span>
            </Label>
            <SimpleAccountSelector
              value={receivedInAccountId}
              onChange={setReceivedInAccountId}
              placeholder="Select bank or cash account..."
              filterAccountIds={paymentAccountIds}
              disabled={isSubmitting}
            />
            <p className="text-xs text-muted-foreground">
              Select the bank or cash account where payment was received
            </p>
          </div>

          {amountDue <= 0 && (
            <div className="bg-warning/5 p-3 rounded-md">
              <p className="text-sm text-warning dark:text-warning">
                This invoice has no outstanding amount. It may already be paid.
              </p>
            </div>
          )}
        </div>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmPayment}
            disabled={isSubmitting || !receivedInAccountId || amountDue <= 0}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Recording Payment...
              </>
            ) : (
              <>
                <CreditCard className="mr-2 h-4 w-4" />
                Confirm Payment
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
